import { useContext, useEffect } from "react";
import { useState } from "react";
import Navbar from "../Navbar";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signInWithPopup,
} from "firebase/auth";
import { auth, googleProvider } from "../../utils/firebase";
import UserContext from "../../contexts/UserContext";
import { setDoc, doc } from "firebase/firestore";
import FrenzyBackground from "../FrenzyBackground";
import { useNavigate } from "react-router-dom";

/**
 * Renders the "/login" page
 * @returns html containing prompts for user login, hides email + pass input fields if user is logged in
 */
const Login = () => {
  let userContext = useContext(UserContext);
  const navigate = useNavigate(); // used by react router to navigate pages

  useEffect(() => {
    document.title = "Login - FPS Aim Trainer";
  }, []);

  // if logged in, immediately navigate to home page
  useEffect(() => {
    if (userContext.loggedIn) {
      navigate("/");
    }
  }, [userContext.loggedIn]);

  const [loginEmail, setLoginEmail] = useState(""); // login/signup email
  const [loginPassword, setLoginPassword] = useState(""); // login/signup password
  const [nick, setNick] = useState(""); // signup nickname
  const [accType, setAccType] = useState("login"); // selected account type

  /**
   * Registering functionality for email + password accounts, makes call to firebase auth method.
   */
  const register = async () => {
    try {
      const credentials = await createUserWithEmailAndPassword(
        auth,
        loginEmail,
        loginPassword
      );
      if (!userContext.db) return;
      await setDoc(doc(userContext.db, "users", credentials.user.uid), {
        emailAddress: loginEmail,
        loginUsername: nick,
        // loginPassword:
        //   "i don't think we should store passwords in the database",
        uID: credentials.user.uid,
      });
    } catch (error: any) {
      console.log(error.message);
    }
  };

  /**
   * Login functionality for email + password accounts, makes call to firebase auth method.
   */
  const login = async () => {
    try {
      await signInWithEmailAndPassword(auth, loginEmail, loginPassword);
    } catch (error: any) {
      console.log(error.message);
    }
  };

  /**
   * Login and/or registering functionality for google accounts, makes call to firebase auth method.
   */
  const googleLogin = async () => {
    try {
      const cred = await signInWithPopup(auth, googleProvider);
      if (!cred || !userContext.db) return;
      await setDoc(doc(userContext.db, "users", cred.user.uid), {
        emailAddress: cred.user.email,
        loginUsername: cred.user.displayName?.split(" ")[0],
        // loginPassword:
        //   "i don't think we should store passwords in the database",
        uID: cred.user.uid,
      });
      userContext.loggedIn = true;
    } catch (error: any) {
      console.log(error.message);
    }
  };

  // radio inputs at top of auth
  const radios = [
    {
      name: "Sign Up",
      value: "register",
    },
    {
      name: "Sign In",
      value: "login",
    },
    {
      name: "Google Auth",
      value: "google",
    },
  ];

  /**
   * Input field creator
   * @param name of the input field
   * @param value of input field
   * @param setter to set new value for input field
   * @returns input field
   */
  const createInput = (
    name: string,
    value: string,
    setter: React.Dispatch<React.SetStateAction<string>>
  ) => (
    <div>
      <label className="block">{name}</label>
      <input
        type="text"
        placeholder={name}
        value={value}
        onChange={(event) => setter(event.target.value)}
        className="w-full px-4 py-2 mt-2 border rounded-md focus:outline-none focus:ring-1 focus:ring-indigo-500"
        required
      />
    </div>
  );

  // buttons to render
  const buttons = [
    {
      name: "Login",
      onClick: login,
      accType: "login",
    },
    {
      name: "Sign up",
      onClick: register,
      accType: "register",
    },
    {
      name: "Google Auth",
      onClick: googleLogin,
      accType: "google",
    },
  ];

  // div to be displayed on top of space scene
  const inner = !userContext.loggedIn && (
    <>
      <div className="px-6 py-4 mt-4 text-left bg-white shadow-lg rounded-md">
        <h3 className="text-3xl font-bold text-center text-indigo-500 pb-4">
          Login to your account
        </h3>
        <div className="flex justify-between items-center mb-4 text-lg gap-4">
          {radios.map((radio) => (
            <div className="flex gap-1 items-center" key={radio.name}>
              <input
                type="radio"
                value={radio.value}
                name="type"
                onChange={(event) => setAccType(event.target.value)}
                checked={accType === radio.value}
              />
              {radio.name}
            </div>
          ))}
        </div>
        <form action="">
          <div className="mt-4">
            {accType === "register" && createInput("Nickname", nick, setNick)}
            {(accType === "login" || accType === "register") && (
              <>
                {createInput("Email", loginEmail, setLoginEmail)}
                {createInput("Password", loginPassword, setLoginPassword)}
              </>
            )}
            <div className="flex items-baseline justify-center space-x-4">
              {buttons.map(
                (button) =>
                  accType === button.accType && (
                    <button
                      type="button"
                      className="px-6 py-2 mt-4 text-white bg-indigo-500 rounded-lg hover:bg-indigo-900"
                      onClick={button.onClick}
                      key={button.name}
                    >
                      {button.name}
                    </button>
                  )
              )}
            </div>
          </div>
        </form>
      </div>
    </>
  );

  return (
    <div className="h-screen flex flex-col">
      <Navbar />
      <div className="w-screen flex-1">
        <FrenzyBackground
          leftBound={-11}
          rightBound={11}
          time={100}
          abs={inner ? inner : null}
        />
      </div>
    </div>
  );
};

export default Login;
